'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, refreshAuthToken } from '@/lib/api/client';
import { clearStoredAccessToken, setStoredAccessToken } from '@/lib/api/auth-storage';

type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  avatarUrl?: string | null;
  roles: string[];
  permissions: string[];
};

const MODULE_ACTIONS = ['view', 'create', 'update', 'delete'] as const;
const MODULES = [
  'clients',
  'cases',
  'documents',
  'tasks',
  'invoices',
  'inventory',
  'team',
] as const;

const OLD_TO_NEW: Record<string, string> = {
  'cases.read': 'cases.view',
  'tasks.read': 'tasks.view',
  'tasks.assign': 'tasks.update',
  'invoices.read': 'invoices.view',
  'clients.read': 'clients.view',
  'documents.read': 'documents.view',
  'documents.upload': 'documents.create',
};

function normalizePermission(perm: string): string {
  return OLD_TO_NEW[perm] ?? perm;
}

function hasModuleViewPermission(user: AuthUser, module: string): boolean {
  if (user.roles.includes('admin')) return true;
  const viewPerm = `${module}.view`;
  const readPerm = `${module}.read`;
  if (user.permissions?.length > 0) {
    const normalized = user.permissions.map(normalizePermission);
    if (normalized.includes('explicit_none')) return false;
    return normalized.includes(viewPerm) || normalized.includes(readPerm);
  }
  return true;
}

type AuthContextValue = {
  user: AuthUser | null;
  session: { accessToken: string | null };
  loading: boolean;
  signUp: (
    _email: string,
    _password: string,
    _metadata?: Record<string, unknown>
  ) => Promise<never>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<AuthUser | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<AuthUser | null>;
  hasPermission: (permission: string) => boolean;
  canModule: (module: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const authMutationRef = useRef(0);
  const userRef = useRef<AuthUser | null>(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const getCurrentUser = async (): Promise<AuthUser | null> => {
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/v1/auth/me', {
        method: 'GET',
        auth: true,
      });

      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    const bootstrapVersion = authMutationRef.current;

    const bootstrap = async () => {
      try {
        const refreshed = await refreshAuthToken();
        if (!mounted) return;
        if (bootstrapVersion !== authMutationRef.current) return;

        if (refreshed) {
          setAccessToken(refreshed);
          setStoredAccessToken(refreshed);
          await getCurrentUser();
        } else if (!userRef.current) {
          setUser(null);
          setAccessToken(null);
          clearStoredAccessToken();
        }
      } catch {
        if (!mounted) return;
        if (bootstrapVersion !== authMutationRef.current) return;
        setUser(null);
        setAccessToken(null);
        clearStoredAccessToken();
      } finally {
        if (mounted) setLoading(false);
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    authMutationRef.current += 1;
    const data = await apiFetch<{
      accessToken: string;
      accessTokenExpiresIn: number;
      user: AuthUser;
    }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    setStoredAccessToken(data.accessToken);
    setAccessToken(data.accessToken);
    setUser(data.user);
  };

  const signOut = async (): Promise<void> => {
    authMutationRef.current += 1;
    try {
      await apiFetch<{ message: string }>('/api/v1/auth/logout', { method: 'POST' });
    } finally {
      clearStoredAccessToken();
      setAccessToken(null);
      setUser(null);
    }
  };

  const signUp = async (): Promise<never> => {
    throw new Error('Sign-up endpoint is not implemented yet');
  };

  const value = useMemo<AuthContextValue>(
    () => {
      const checkPermission = (permission: string) => {
        if (!user) return false;
        if (user.roles.includes('admin')) return true;

        const normalized = normalizePermission(permission);

        if (user.permissions?.length > 0) {
          const normalizedUserPerms = user.permissions.map(normalizePermission);
          if (normalizedUserPerms.length === 1 && normalizedUserPerms[0] === 'explicit_none') {
          } else if (normalizedUserPerms.includes(normalized)) {
            return true;
          } else {
            const action = normalized.split('.')[1] as (typeof MODULE_ACTIONS)[number] | undefined;
            if (action && MODULE_ACTIONS.includes(action) && action !== 'view') {
              const viewPerm = normalized.replace(/\.(create|update|delete)$/, '.view');
              if (!normalizedUserPerms.includes(viewPerm)) return false;
            }
            return false;
          }
        }

        const roleDefaultPerms: Record<string, string[]> = {
          client: [
            'clients.view',
            'cases.view',
            'documents.view',
            'tasks.view',
            'invoices.view',
            'inventory.view',
            'team.view',
          ],
          agent: [
            'clients.view',
            'clients.create',
            'clients.update',
            'cases.view',
            'cases.create',
            'cases.update',
            'documents.view',
            'documents.create',
            'documents.delete',
            'tasks.view',
            'tasks.create',
            'tasks.update',
            'invoices.view',
            'invoices.create',
            'invoices.update',
            'invoices.delete',
            'team.view',
            'inventory.view',
            'inventory.create',
            'inventory.update',
            'inventory.delete',
          ],
          paralegal: [
            'clients.view',
            'cases.view',
            'documents.view',
            'documents.create',
            'tasks.view',
            'tasks.update',
            'invoices.view',
            'invoices.update',
            'team.view',
            'inventory.view',
            'inventory.create',
            'inventory.update',
          ],
          viewer: ['clients.view', 'cases.view', 'documents.view', 'tasks.view', 'team.view', 'inventory.view'],
          consultant: [
            'cases.view',
            'cases.create',
            'cases.update',
            'documents.view',
            'documents.create',
            'documents.delete',
            'tasks.view',
            'tasks.create',
            'tasks.update',
            'invoices.view',
            'invoices.create',
            'invoices.update',
            'invoices.delete',
          ],
        };

        const rolePerms = roleDefaultPerms[user.roles[0]];
        if (rolePerms?.includes(normalized)) return true;

        const action = normalized.split('.')[1] as (typeof MODULE_ACTIONS)[number] | undefined;
        if (action && MODULE_ACTIONS.includes(action) && action !== 'view') {
          const viewPerm = normalized.replace(/\.(create|update|delete)$/, '.view');
          const createPerm = normalized.replace(/\.(update|delete)$/, '.create');
          if (rolePerms?.includes(viewPerm)) {
            if (action === 'update' && rolePerms.includes(createPerm)) return true;
            if (action === 'delete' && rolePerms.includes(createPerm)) return true;
          }
        }

        return false;
      };

      return {
        user,
        session: { accessToken },
        loading,
        signUp,
        signIn,
        signOut,
        getCurrentUser,
        isEmailVerified: () => Boolean(user),
        getUserProfile: async () => user,
        hasPermission: checkPermission,
        canModule: (module: string) => {
          if (!user) return false;
          if (user.roles.includes('admin')) return true;
          return checkPermission(`${module}.view`) || checkPermission(`${module}.read`);
        },
      };
    },
    [user, accessToken, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
